/*
 * First order offer. sections/peanut-signup-offer.liquid.
 *
 * Three ways the dialog opens, in this order:
 *
 * 1. The page came back from a signup. Shopify's customer form posts to
 *    /contact#<form id> and redirects to the page it came from with
 *    ?customer_posted=true, and the browser carries the hash across the
 *    redirect. If that hash is this dialog's form or the footer's, the visitor
 *    has just given their email: open on the done panel, show the code and put
 *    it on the cart. Both forms tag the customer, so both earn the code.
 * 2. The page came back to this form without customer_posted: Shopify refused
 *    the address. Open on the form, where Liquid has printed the error.
 * 3. Otherwise, once: after the delay in the section's setting or halfway down
 *    the page, whichever is first, and only if nothing else is open. A visitor
 *    from an ad gets the shorter ad delay, on this page and the rest of the
 *    visit: the ad promised the 10%. Never by itself on the cart page.
 * 4. Any time, from an offer line (snippets/peanut-offer-link.liquid) under
 *    Add to cart, in the cart sheet or on the cart page. Those lines show only
 *    while .peanut-offer-ready is on <html>, which this file sets.
 *
 * A signup can come back without the hash. Shopify may stop the post at
 * /challenge for a CAPTCHA, and the post that leaves that page returns to the
 * product page with customer_posted=true and no hash. So a submit of either
 * form also leaves its id in sessionStorage, and that counts as the hash. On
 * /challenge itself this file does nothing at all; the section is not rendered
 * there either, this is the second lock.
 *
 * It stays shut for anyone who signed up before (localStorage, for good), who
 * closed it in the last seven days, who has seen it this visit, or who came in
 * to write a review: #write-review is the card in the box, so they have bought.
 * Storage can throw in a private window; every read and write is guarded, and
 * without storage the worst case is the offer once per page load.
 *
 * The code goes on the cart through /cart/update.js and its discount field,
 * which Shopify added in 2025. If the cart does not come back carrying it, the
 * older /discount/<code> route sets it for checkout instead. Neither is checked
 * against the customer here: Shopify does that at checkout, against the email
 * typed there, which is the whole point of the segment.
 *
 * Opening and closing reuse the cart sheet's shape: showModal, transitions on
 * [open] with @starting-style, and a class for the way out.
 */
(function () {
  'use strict';

  if (window.location.pathname.indexOf('/challenge') === 0) return;

  var dialog = document.querySelector('[data-peanut-offer]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CODE = dialog.getAttribute('data-code') || '';
  var FORMS = (dialog.getAttribute('data-forms') || '').split(',');
  var OWN_FORM = FORMS[0];
  var DELAY = (parseInt(dialog.getAttribute('data-delay'), 10) || 10) * 1000;
  var AD_DELAY = (parseInt(dialog.getAttribute('data-ad-delay'), 10) || 3) * 1000;
  var AUTO = dialog.getAttribute('data-auto') !== 'false';
  var PAGE = dialog.getAttribute('data-page') || '';
  var AD_SOURCES = /^(meta|facebook|fb|instagram|ig|ig_ads|fb_ads|an)$/i;
  var KEY = 'peanut-offer';
  var WEEK = 7 * 24 * 60 * 60 * 1000;

  function read(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* private window */ }
  }

  // Remember which of our forms was sent, for a return that has lost its hash.
  document.addEventListener('submit', function (event) {
    var id = event.target && event.target.getAttribute && event.target.getAttribute('id');
    if (id && FORMS.indexOf(id) !== -1) write('sessionStorage', KEY + '-pending', id);
  }, true);

  var url = new URL(window.location.href);
  var hash = url.hash.replace('#', '');
  var posted = url.searchParams.get('customer_posted') === 'true';
  var pending = read('sessionStorage', KEY + '-pending') || '';
  try { window.sessionStorage.removeItem(KEY + '-pending'); } catch (e) { /* ignore */ }
  var fromSignup = posted && (FORMS.indexOf(hash) !== -1 || FORMS.indexOf(pending) !== -1);
  var refused = !posted && hash === OWN_FORM;

  // Take the signup's traces out of the address, so a reload or a shared link
  // does not replay the done panel.
  if (fromSignup || refused) {
    url.searchParams.delete('customer_posted');
    url.hash = '';
    try { window.history.replaceState({}, '', url.toString()); } catch (e) { /* ignore */ }
  }

  function showDone() {
    dialog.querySelector('[data-peanut-offer-ask]').hidden = true;
    dialog.querySelector('[data-peanut-offer-done]').hidden = false;
  }

  var applied = null;

  function applyCode() {
    if (!CODE || !window.fetch) return;
    applied = fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: CODE })
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) {
        var codes = (cart && cart.discount_codes) || [];
        var held = codes.some(function (entry) {
          return entry && String(entry.code).toUpperCase() === CODE.toUpperCase();
        });
        if (!held) {
          return fetch(ROOT + 'discount/' + encodeURIComponent(CODE) + '?redirect=' + encodeURIComponent(ROOT + 'cart.js'));
        }
      })
      .catch(function () { /* the code is on screen; the visitor can still type it */ });
  }

  function open(focusSelector) {
    if (dialog.open) return;
    // showModal stacks above the cart sheet when that is open, which is what an
    // offer line inside the sheet wants: closing the offer leaves the sheet.
    dialog.showModal();
    write('sessionStorage', KEY, 'shown');
    var target = dialog.querySelector(focusSelector);
    if (target) target.focus();
  }

  /*
   * Same exit as the cart sheet: a class runs the transition and the dialog
   * only really closes when it has finished, or after 320ms if nothing ran.
   * transitionend bubbles, so only the dialog's own counts.
   */
  function close() {
    if (!dialog.open || dialog.classList.contains('is-closing')) return;
    if (dialog.querySelector('[data-peanut-offer-done]').hidden) {
      write('localStorage', KEY, 'closed:' + Date.now());
    }
    dialog.classList.add('is-closing');

    var done = false;
    function finish() {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      dialog.classList.remove('is-closing');
      dialog.close();
    }
    function onEnd(event) {
      if (event.target === dialog && !event.pseudoElement) finish();
    }
    dialog.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, 320);
  }

  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) return close();
    if (event.target.closest && event.target.closest('[data-peanut-offer-close]')) close();
  });

  // The offer lines. Delegated, because the cart sheet swaps its contents in
  // from a section render and a listener on the old button would go with it.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-peanut-offer-open]');
    if (!trigger) return;
    event.preventDefault();
    open('#peanut-offer-email');
  });

  dialog.addEventListener('cancel', function (event) {
    event.preventDefault();
    close();
  });

  var copy = dialog.querySelector('[data-peanut-offer-copy]');
  if (copy) {
    if (!navigator.clipboard) {
      copy.hidden = true;
    } else {
      copy.addEventListener('click', function () {
        navigator.clipboard.writeText(CODE).then(function () {
          var label = copy.querySelector('span');
          var was = label.textContent;
          label.textContent = copy.getAttribute('data-copied-label') || was;
          window.setTimeout(function () { label.textContent = was; }, 2000);
        });
      });
    }
  }

  if (fromSignup) {
    write('localStorage', KEY, 'joined');
    showDone();
    applyCode();
    open('[data-peanut-offer-done-title]');
    // The cart page was rendered before the code went on. Once the visitor is
    // done here, render it again so the 10% shows in the sums.
    if (PAGE === 'cart') {
      dialog.addEventListener('close', function () {
        (applied || Promise.resolve()).then(function () { window.location.reload(); });
      }, { once: true });
    }
    return;
  }

  // Box card visitors have bought, so the segment would refuse them anyway.
  var boxCard = hash === 'write-review' || hash === 'reviews' ||
    url.searchParams.get('utm_source') === 'box-card' ||
    read('sessionStorage', KEY) === 'skipped';
  if (boxCard) write('sessionStorage', KEY, 'skipped');

  var stored = read('localStorage', KEY) || '';
  if (stored !== 'joined' && !boxCard) {
    document.documentElement.classList.add('peanut-offer-ready');
  }

  if (refused) {
    open('#peanut-offer-email');
    return;
  }

  // Everything below decides whether to open it by itself.
  if (!AUTO || boxCard) return;
  if (stored === 'joined') return;
  if (stored.indexOf('closed:') === 0 && Date.now() - parseInt(stored.slice(7), 10) < WEEK) return;
  if (read('sessionStorage', KEY)) return;

  var fromAd = !!url.searchParams.get('fbclid') ||
    AD_SOURCES.test(url.searchParams.get('utm_source') || '') ||
    read('sessionStorage', KEY + '-ad') === '1';
  if (fromAd) write('sessionStorage', KEY + '-ad', '1');

  var timer = null;
  var fired = false;

  function somethingElseIsOpen() {
    if (document.querySelector('dialog[open]')) return true;
    var review = document.querySelector('.jdgm-write-review-modal');
    return !!review && window.getComputedStyle(review).display !== 'none';
  }

  function onScroll() {
    var depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
    if (depth >= 0.5) trigger();
  }

  function trigger() {
    if (fired) return;
    if (somethingElseIsOpen()) {
      // Try again once things are quiet, rather than stacking on the cart
      // sheet or the review form.
      window.clearTimeout(timer);
      timer = window.setTimeout(trigger, 5000);
      return;
    }
    fired = true;
    window.clearTimeout(timer);
    window.removeEventListener('scroll', onScroll);
    open('#peanut-offer-title');
  }

  timer = window.setTimeout(trigger, fromAd ? AD_DELAY : DELAY);
  window.addEventListener('scroll', onScroll, { passive: true });
})();
